import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Share2,
  Copy,
  Mail,
  MessageCircle,
  Send,
  Smartphone,
  QrCode,
  FacebookIcon,
  LinkedinIcon,
  Link2,
} from 'lucide-react';

interface ReferralLink {
  id: string;
  name: string;
  url: string;
  clicks: number;
  signups: number;
  conversions: number;
  createdAt: string;
  isActive: boolean;
}

interface ReferralLinksSharingProps {
  mainReferralLink: ReferralLink;
}

const ReferralLinksSharing = ({
  mainReferralLink,
}: ReferralLinksSharingProps) => {
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState('');

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(url);
      setTimeout(() => setCopiedLink(''), 3000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleShareEmail = (url: string) => {
    const subject = 'Join our Forex Trading Platform';
    const body = `I'd like to invite you to join our forex trading platform. Sign up using my referral link: ${url}`;
    window.open(
      `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    );
  };

  const handleShareWhatsApp = (url: string) => {
    const message = `Join our forex trading platform! Sign up here: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleShareTelegram = (url: string) => {
    const message = `Join our forex trading platform! Sign up here: ${url}`;
    window.open(
      `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(message)}`,
      '_blank'
    );
  };

  const handleShareSMS = (url: string) => {
    const message = `Join our forex trading platform! Sign up here: ${url}`;
    window.open(`sms:?body=${encodeURIComponent(message)}`);
  };

  const handleShareFacebook = (url: string) => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  const handleShareTwitter = (url: string) => {
    const text = 'Join our forex trading platform! Sign up here:';
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  const handleShareLinkedIn = (url: string) => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  const generateQRCode = (url: string) => {
    window.open(
      `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4 w-full">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Referral Links & Sharing
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Share your referral links across multiple platforms to attract new
              clients
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowReferralModal(!showReferralModal)}
          >
            <Share2 className="h-4 w-4 mr-2" />
            {showReferralModal ? 'Hide Sharing' : 'Show Sharing'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showReferralModal && (
          <div className="space-y-6">
            {/* Main Referral Link */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h5 className="font-medium">{mainReferralLink.name}</h5>
                    <Badge
                      variant={
                        mainReferralLink.isActive ? 'default' : 'secondary'
                      }
                    >
                      {mainReferralLink.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <Link2 className="h-3 w-3" />
                    <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                      {mainReferralLink.url}
                    </code>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <span className="text-gray-500">Clicks:</span>
                      <span className="font-semibold ml-1">
                        {mainReferralLink.clicks}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Signups:</span>
                      <span className="font-semibold ml-1">
                        {mainReferralLink.signups}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Conversions:</span>
                      <span className="font-semibold ml-1">
                        {mainReferralLink.conversions}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* All Sharing Options */}
              <div className="border-t pt-3">
                <p className="text-xs font-medium text-gray-700 mb-2">
                  Share this link:
                </p>
                <div className="flex flex-wrap gap-2">
                  {/* Copy Link */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopyLink(mainReferralLink.url)}
                    className="flex items-center gap-1 border-gray-200 text-gray-600 hover:bg-gray-50"
                  >
                    <Copy className="h-4 w-4 text-gray-600" />
                    {copiedLink === mainReferralLink.url ? 'Copied!' : 'Copy'}
                  </Button>

                  {/* Email */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShareEmail(mainReferralLink.url)}
                    className="flex items-center gap-1 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                  >
                    <Mail className="h-4 w-4 text-red-600" />
                    Gmail
                  </Button>

                  {/* WhatsApp */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShareWhatsApp(mainReferralLink.url)}
                    className="flex items-center gap-1 border-green-200 text-green-600 hover:bg-green-50 hover:border-green-300"
                  >
                    <MessageCircle className="h-4 w-4 text-green-600" />
                    <span className="ml-1">WhatsApp</span>
                  </Button>

                  {/* Telegram */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShareTelegram(mainReferralLink.url)}
                    className="flex items-center gap-1 border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300"
                  >
                    <Send className="h-4 w-4 text-blue-600" />
                    <span className="ml-1">Telegram</span>
                  </Button>

                  {/* SMS */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShareSMS(mainReferralLink.url)}
                    className="flex items-center gap-1 border-purple-200 text-purple-600 hover:bg-purple-50 hover:border-purple-300"
                  >
                    <Smartphone className="h-4 w-4 text-purple-600" />
                    <span className="ml-1">SMS</span>
                  </Button>

                  {/* QR Code */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => generateQRCode(mainReferralLink.url)}
                    className="flex items-center gap-1 border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300"
                  >
                    <QrCode className="h-4 w-4 text-indigo-600" />
                    <span className="ml-1">QR Code</span>
                  </Button>

                  {/* Facebook */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShareFacebook(mainReferralLink.url)}
                    className="flex items-center justify-center gap-1 border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300"
                  >
                    <FacebookIcon className="h-4 w-4" />
                    <span className="ml-1">Facebook</span>
                  </Button>

                  {/* X (Twitter) */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShareTwitter(mainReferralLink.url)}
                    className="flex items-center gap-1 border-sky-200 text-sky-600 hover:bg-sky-50 hover:border-sky-300"
                  >
                    <span className="h-4 w-4 mb-2 font-bold text-sky-600">
                      𝕏
                    </span>
                    <span className="ml-1">X</span>
                  </Button>

                  {/* LinkedIn */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShareLinkedIn(mainReferralLink.url)}
                    className="flex items-center justify-center gap-1 border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300"
                  >
                    <LinkedinIcon className="h-4 w-4" />{' '}
                    <span className="ml-1">LinkedIn</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferralLinksSharing;
